import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "antd";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ActionModal from "../../../modals/ActionModal";
import ConfirmModal from "../../../modals/ConfirmModal";
import SuccessModal from "../../../modals/SuccessModal";
import Separator from "../../../shared/Separator";
import {
  setIsAccountDataFilled,
  setProfilePicUrl,
  setUserData,
  setAuthMethod,
} from "../../../store/common";
import {
  getCompany,
  getNotificationPreferences,
  getUser,
  resetPassword,
  saveProfile,
  setNotificationPreferences,
  uploadUserProfilePic,
  closeAccount,
  submitReferral,
} from "../../../utils/users";
import { getAuthenticationMethod } from "../../../utils/auth";
import { isAccountDataComplete } from "../../../utils/accountDataValidation";
import ProfileBillingNotice from "../../../components/ProfileBillingNotice";

const MyProfile = ({ onProfileUpdate }) => {
  const { isLoading: authLoading, getIdTokenClaims, logout } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [profileData, setProfileData] = useState({});
  const [companyData, setCompanyData] = useState({});
  const [notificationPreference, setNotificationPreference] = useState({});
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isReferralButtonLoading, setIsReferralButtonLoading] = useState(false);
  const [authMethodLoading, setAuthMethodLoading] = useState(true);
  // The state below is no longer needed as the disabled status is directly derived from profileData.referralCodeUsed
  // const [isReferralButtonDisabled, setIsReferralButtonDisabled] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralCodeError, setReferralCodeError] = useState(false);

  const fileInputRef = useRef(null);

  const isAccountDataFilled = useSelector(
    (state) => state.commonReducer.isAccountDataFilled
  );
  const profilePicture = useSelector(
    (state) => state.commonReducer.profilePicUrl
  );
  const authMethod = useSelector(
    (state) => state.commonReducer.authMethod
  );
  const dispatch = useDispatch();

  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    company_name: false,
    company_tax_number: false,
    company_phone: false,
    company_address: false,
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      setIsDataLoading(true);
      (async () => {
        try {
          const token = await getIdTokenClaims();
          const [userDataRes, companyRes] = await Promise.all([
            getUser(token.__raw),
            getCompany(token.__raw),
          ]);

          setProfileData(userDataRes.data);
          if (userDataRes.data?.picture) {
            dispatch(setProfilePicUrl(userDataRes.data.picture));
          }

          const row = companyRes.data?.company;
          const fromCompany =
            row && typeof row === "object" && !Array.isArray(row) ? { ...row } : {};
          const fromProfile = userDataRes.data || {};
          // `/profile` carries phone + company name from `users` (complete-profile
          // save). `/company-info` reads `companies`. Merge so Settings shows both
          // after complete profile and for older rows missing `companies` sync.
          setCompanyData({
            ...fromCompany,
            company_name:
              fromCompany.company_name || fromProfile.company_name || "",
            company_phone:
              fromCompany.company_phone || fromProfile.company_phone || "",
          });

          await getNotificationPreferencesAPI();
        } catch (error) {
          console.error("Error loading settings profile:", error);
        } finally {
          setIsDataLoading(false);
        }
      })();
    }
  }, [authLoading, dispatch]);

  // Keep billing gate in sync (same rules as Settings parent fetch)
  useEffect(() => {
    dispatch(
      setIsAccountDataFilled(isAccountDataComplete(profileData, companyData))
    );
  }, [profileData, companyData, dispatch]);

  // Detect authentication method — use decoded claims directly (reliable across all browsers)
  useEffect(() => {
    const detectAuthMethod = async () => {
      try {
        const tokenClaims = await getIdTokenClaims();
        const method = getAuthenticationMethod(tokenClaims);
        dispatch(setAuthMethod(method));
      } catch (error) {
        console.error("Error detecting auth method:", error);
      } finally {
        setAuthMethodLoading(false);
      }
    };

    if (!authLoading) {
      detectAuthMethod();
    }
  }, [authLoading]);

  const validateMandatoryFields = () => {
    const errors = {
      name: !String(profileData.name || "").trim(),
      company_name: !String(companyData.company_name || "").trim(),
      company_tax_number: !String(companyData.company_tax_number || "").trim(),
      company_phone: !String(companyData.company_phone || "").trim(),
      company_address: !String(companyData.company_address || "").trim(),
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const getNotificationPreferencesAPI = async () => {
    try {
      const token = await getIdTokenClaims();
      const {
        data: { data },
      } = await getNotificationPreferences(token.__raw);
      setNotificationPreference(data);
      return data;
    } catch (error) {
      console.error("Error fetching notification preferences:", error);
      setNotificationPreference([]);
      throw error;
    }
  };

  const notificationPreferencesAPI = async (id, status) => {
    const newNotificationPref = notificationPreference.map((item) => {
      if (item.id === id) {
        item.notification_enabled = status;
      }
      return item;
    });
    setNotificationPreference(newNotificationPref);
    const token = await getIdTokenClaims();
    try {
      await setNotificationPreferences(token.__raw, id, status);
      getNotificationPreferencesAPI();
    } catch (e) {
      console.log(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setShowValidationErrors(true);
    const isValid = validateMandatoryFields();
    if (!isValid) {
      enqueueSnackbar("Please fill all mandatory fields marked with *", {
        variant: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await getIdTokenClaims();

      saveProfile(token.__raw, profileData, companyData)
        .then((res) => {
          console.log(res.data);
          setSuccessMessage("Successfully updated your profile!");
          setSuccessModalOpen(true);
          // Call the parent component's update function to refresh the account data status
          if (typeof onProfileUpdate === "function") {
            onProfileUpdate();
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } catch (e) {
      console.error(e);
      setIsSubmitting(false);
    }
  };

  const handleSetNewPassword = () => {
    if (authMethod.isSocialLogin || authMethodLoading) {
      return;
    }
    setResetPasswordModalOpen(true);
  };

  const onConfirmResetPassword = async () => {
    setResetPasswordModalOpen(false);
    try {
      const token = await getIdTokenClaims();
      const res = await resetPassword(token.__raw);
      if (res.data.error === 1) {
        enqueueSnackbar(
          "Something went wrong while resetting the password. Please contact support!",
          { variant: "error" }
        );
      } else if (res.data.error === 0) {
        enqueueSnackbar(
          "Password reset link has been sent to your registered email address.",
          { variant: "success" }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        "Something went wrong while resetting the password. Please contact support!",
        { variant: "error" }
      );
      console.error(err);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      enqueueSnackbar("Please select a valid image file (JPEG, PNG, or GIF)", {
        variant: "error",
      });
      return;
    }

    setIsUploadingPicture(true);

    try {
      const token = await getIdTokenClaims();
      const response = await uploadUserProfilePic(token.__raw, file);

      if (response.data && response.data.picture_url) {
        dispatch(setProfilePicUrl(response.data.picture_url));
        setSuccessMessage("Profile picture updated successfully!");
        setSuccessModalOpen(true);

        // Update profile data with new picture URL
        dispatch(
          setUserData({
            ...profileData,
            picture: response.data.picture_url,
          })
        );

        // Call the parent component's update function to refresh the account data status
        if (typeof onProfileUpdate === "function") {
          onProfileUpdate();
        }
      } else {
        setSuccessMessage(response.data.message);
        setSuccessModalOpen(true);

        // alert(response.data.message);
      }
    } catch (error) {
      console.log("Error uploading profile picture:", error);
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const onClickDelete = () => {
    setOpenDeleteModal(true);
  };
  const onClickReferral = async () => {
    if (!String(referralCode || "").trim()) {
      setReferralCodeError(true);
      return;
    }
    setReferralCodeError(false);
    try {
      setIsReferralButtonLoading(true);
      const token = await getIdTokenClaims();
      const response = await submitReferral(token.__raw, referralCode);
      if (response.data && response.data.success === true) {
        setProfileData({
          ...profileData,
          referralCodeUsed: 1, // Set referralCodeUsed to 1 on successful submission
        });
        setSuccessMessage(response.data.message);
        setSuccessModalOpen(true);
        setReferralCode(""); // Clear the referral code input after successful submission
      } else {
        setSuccessMessage(response.data.message);
        setSuccessModalOpen(true);
      }
    } catch (error) {
      setSuccessMessage(error.message);
      setSuccessModalOpen(true);
    } finally {
      setIsReferralButtonLoading(false);
    }
  };

  const onConfirmDeleteAccount = async (e) => {
    setIsModalLoading(true);
    try {
      const token = await getIdTokenClaims();
      const response = await closeAccount(token.__raw);
      if (response.data && response.data.success === true) {
        dispatch(setProfilePicUrl(response.data.picture_url)); // This line might be incorrect, `picture_url` may not be relevant after account deletion. Consider removing or adjusting.
        setSuccessMessage(response.data.message);
        setSuccessModalOpen(true);

        // Force Logout user
        setTimeout(() => {
          logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
        }, 4000);
      } else {
        setSuccessMessage(response.data.message);
        setSuccessModalOpen(true);
        // alert(response.data.message);
      }
    } catch (error) {
      console.log("Error uploading profile picture:", error); // This error message seems to be for picture upload, not account deletion. Adjust if necessary.
      setSuccessMessage(error.message);
      setSuccessModalOpen(true);
    } finally {
      setOpenDeleteModal(false);
      setIsModalLoading(false);
    }
  };

  // Determine if the referral input and button should be disabled
  const isReferralSectionDisabled = profileData.referralCodeUsed === 1;

  return (
    <>
      {!isDataLoading ? (
        <ProfileBillingNotice companyData={companyData} />
      ) : null}
      {isDataLoading ? (
        <div className="w-full flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6 mb-8 items-start">
          {/* Account data + profile photo */}
          <div className="border border-gray-300 rounded-2xl p-6 text-sm">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-gray-200">
                <div className="text-lg font-medium">Account Data</div>
                <div className="flex items-center flex-shrink-0">
                  <div className="relative">
                    {isUploadingPicture ? (
                      <div className="w-16 h-16 rounded-full flex justify-center items-center bg-gray-100 border border-gray-200">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500" />
                      </div>
                    ) : (
                      <img
                        src={profilePicture}
                        alt="Profile"
                        className="w-16 h-16 rounded-full object-cover cursor-pointer border-2 border-white shadow-sm ring-1 ring-gray-200"
                        onClick={handlePictureClick}
                      />
                    )}
                    <button
                      type="button"
                      className="absolute -bottom-0.5 -right-0.5 bg-[#007BFF] text-white rounded-full w-7 h-7 flex items-center justify-center shadow hover:bg-[#0056b3]"
                      onClick={handlePictureClick}
                      aria-label="Change profile photo"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div className={"text-sm text-gray-700 mb-3"}>
                        <p>
                          Full name <span className="text-red-500">*</span>
                        </p>
                        <input
                          type="text"
                          placeholder={"Enter full name"}
                          className={`rounded-lg px-3 py-2 border ${
                            showValidationErrors && fieldErrors.name
                              ? "border-red-500"
                              : "border-gray-300"
                          } mt-1 w-full`}
                          value={profileData.name || ""}
                          onChange={(e) => {
                            setProfileData({
                              ...profileData,
                              name: e.target.value,
                            });
                          }}
                          required
                        />
                        {showValidationErrors && fieldErrors.name && (
                          <p className="text-red-500 text-xs mt-1">
                            Full name is required
                          </p>
                        )}
                      </div>
                      <div className={"text-sm text-gray-700 mb-4"}>
                        <p>
                          Email <span className="text-red-500">*</span>
                        </p>
                        <input
                          type="text"
                          placeholder={"Enter your email"}
                          className={
                            "rounded-lg px-3 py-2 border border-gray-300 mt-1 w-full disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                          }
                          value={profileData.email}
                          disabled
                        />
                      </div>
                      <div className={"text-sm text-gray-700 mb-4"}>
                        <p>
                          Company Name <span className="text-red-500">*</span>
                        </p>
                        <input
                          type="text"
                          placeholder={"Enter your company name"}
                          className={`rounded-lg px-3 py-2 border ${
                            showValidationErrors && fieldErrors.company_name
                              ? "border-red-500"
                              : "border-gray-300"
                          } mt-1 w-full`}
                          value={companyData.company_name || ""}
                          onChange={(e) => {
                            setCompanyData({
                              ...companyData,
                              company_name: e.target.value,
                            });
                          }}
                          required
                        />
                        {showValidationErrors && fieldErrors.company_name && (
                          <p className="text-red-500 text-xs mt-1">
                            Company name is required
                          </p>
                        )}
                      </div>
                      <div className={"text-sm text-gray-700 mb-4"}>
                        <p>
                          Company Registration Number{" "}
                        </p>
                        <input
                          type="text"
                          placeholder={"Enter company registration number"}
                          className={`rounded-lg px-3 py-2 border border-gray-300 mt-1 w-full`}
                          value={companyData.company_registration_number || ""}
                          onChange={(e) => {
                            setCompanyData({
                              ...companyData,
                              company_registration_number: e.target.value,
                            });
                          }}
                        />
                      </div>
                      <div className={"text-sm text-gray-700 mb-4"}>
                        <p>
                          Company Tax Number{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        <input
                          type="text"
                          placeholder={"Enter company Tax Number"}
                          className={`rounded-lg px-3 py-2 border ${
                            showValidationErrors && fieldErrors.company_tax_number
                              ? "border-red-500"
                              : "border-gray-300"
                          } mt-1 w-full`}
                          value={companyData.company_tax_number || ""}
                          onChange={(e) => {
                            setCompanyData({
                              ...companyData,
                              company_tax_number: e.target.value,
                            });
                          }}
                          required
                        />
                        {showValidationErrors && fieldErrors.company_tax_number && (
                          <p className="text-red-500 text-xs mt-1">
                            Company tax number is required
                          </p>
                        )}
                      </div>

                      <div className={"text-sm text-gray-700 mb-4"}>
                        <p>
                          Company Phone <span className="text-red-500">*</span>
                        </p>
                        <input
                          type="tel"
                          placeholder={"Enter company phone"}
                          className={`rounded-lg px-3 py-2 border ${
                            showValidationErrors && fieldErrors.company_phone
                              ? "border-red-500"
                              : "border-gray-300"
                          } mt-1 w-full`}
                          value={companyData.company_phone || ""}
                          onChange={(e) => {
                            setCompanyData({
                              ...companyData,
                              company_phone: e.target.value,
                            });
                          }}
                          required
                        />
                        {showValidationErrors && fieldErrors.company_phone && (
                          <p className="text-red-500 text-xs mt-1">
                            Company phone is required
                          </p>
                        )}
                      </div>
                      <div className={"text-sm text-gray-700 md:col-span-2"}>
                        <p>
                          Company Address{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        <textarea
                          placeholder={"Enter company address"}
                          rows={3}
                          className={`rounded-lg px-3 py-2 border ${
                            showValidationErrors && fieldErrors.company_address
                              ? "border-red-500"
                              : "border-gray-300"
                          } mt-1 block w-full min-h-[5rem]`}
                          value={companyData.company_address || ""}
                          onChange={(e) => {
                            setCompanyData({
                              ...companyData,
                              company_address: e.target.value,
                            });
                          }}
                          required
                        />
                        {showValidationErrors && fieldErrors.company_address && (
                          <p className="text-red-500 text-xs mt-1">
                            Company address is required
                          </p>
                        )}
                      </div>
              </div>
              <Separator className="my-6" />
              <div>
                  <div className={"text-lg font-medium"}>
                    Email notifications
                  </div>
                  <div className={"mt-4"}>
                    {notificationPreference.length
                      ? notificationPreference.map((item, index) => (
                          <div
                            key={item.id}
                            className={`flex py-5 justify-between text-sm ${
                              index === 0 ? "border-t" : ""
                            } border-b border-gray-200`}
                          >
                            <div>{item.name}</div>
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={item.notification_enabled}
                                onChange={(e) => {
                                  notificationPreferencesAPI(
                                    item.id,
                                    !item.notification_enabled
                                  );
                                }}
                                className="sr-only peer"
                              />
                              <div className="relative w-9 h-3 bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[90%] rtl:peer-checked:after:-translate-x-full peer-checked:after:border-blue-500 after:content-[''] after:absolute after:-top-[3.5px] after:-start-[1px] after:rounded-full after:h-5 after:w-5 after:transition-all after:bg-white after:shadow-sm after:border peer-checked:after:bg-blue-500 peer-checked:bg-blue-300"></div>
                            </label>
                          </div>
                        ))
                      : ""}
                  </div>
                  <Separator />
                  <div
                    className={
                      "w-full flex items-center justify-end gap-3 text-sm"
                    }
                  >
                    <div className="flex-1">
                      {showValidationErrors && !isAccountDataFilled && (
                        <p className="text-red-500 text-sm">
                          * Please fill all mandatory fields
                        </p>
                      )}
                    </div>
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={isSubmitting}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
            </form>
          </div>

          {/* Referral, password, close account */}
          <div className="flex flex-col gap-4">
            <div className="border border-gray-300 rounded-2xl p-6 text-sm">
              <b className="block text-base mb-2">Referral Code Redemption.</b>
              <p className="text-gray-600 mb-3">
                Enter your referral code for extra benefits.
              </p>
              <input
                type="text"
                disabled={isReferralSectionDisabled}
                placeholder="Enter referral code"
                className={`rounded-lg px-3 py-2 border w-full ${
                  referralCodeError ? "border-red-500" : "border-gray-300"
                } ${isReferralSectionDisabled ? "bg-gray-200 cursor-not-allowed" : ""}`}
                value={referralCode}
                onChange={(e) => {
                  setReferralCode(e.target.value);
                  if (referralCodeError) setReferralCodeError(false);
                }}
              />
              {referralCodeError && (
                <p className="text-red-500 text-xs mt-1">Referral code is required</p>
              )}
              <Button
                onClick={onClickReferral}
                className="mt-3 w-full"
                type="primary"
                loading={isReferralButtonLoading}
                disabled={isReferralSectionDisabled}
              >
                Submit referral
              </Button>
            </div>
            {!authMethod.isSocialLogin && (
              <div className="border border-gray-300 rounded-2xl p-6 text-sm">
                <p className="text-gray-700 mb-1">
                  Update your password through the button below
                </p>
                <p className="text-gray-600 mb-4 text-xs">
                  We&apos;ll email a reset link to your registered email address.
                  Open the link and follow the steps.
                </p>
                <button
                  disabled={authMethodLoading}
                  type="button"
                  className="flex items-center justify-center w-full font-medium text-sm px-3 py-2 rounded-lg bg-white border border-[#007BFF] text-[#007BFF] hover:bg-blue-50 transition-colors"
                  onClick={handleSetNewPassword}
                >
                  Set new password
                </button>
              </div>
            )}
            <div className="border border-gray-300 rounded-2xl p-6 text-sm">
              <b className="block text-base mb-2">Close your account</b>
              <p className="text-gray-600 mb-4">
                All your data and backup will be removed
              </p>
              <Button onClick={onClickDelete} danger className="w-full">
                Close account
              </Button>
            </div>
          </div>
        </div>
      )}

          <SuccessModal
            title="Profile update"
            text={successMessage}
            modalOpen={successModalOpen}
            onModalClose={() => {
              setSuccessModalOpen(false);
            }}
          />
          <ActionModal
            isOpen={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            onConfirm={onConfirmDeleteAccount}
            itemName={""}
            actionType="deleteAccount"
            title="Delete Account Permanently"
            isLoading={isModalLoading}
          />
          <ConfirmModal
            title="Reset password?"
            text="We'll email a reset link to your registered email address. Open the link and follow the steps to choose a new password."
            subText="Are you sure you want to continue?"
            confirmButtonText="Send reset link"
            modalOpen={resetPasswordModalOpen}
            onCancel={() => setResetPasswordModalOpen(false)}
            onOkay={onConfirmResetPassword}
          />
    </>
  );
};

export default MyProfile;